import { HttpClient, HttpContext } from "@angular/common/http";
import { EnvironmentService } from "./environment.service";
import { SILENT_REQUEST } from "../interceptors/silent-request.token";
import { VCPM } from 'pcs-domain';
import { Observable } from "rxjs";
import { Injectable } from "@angular/core";
import { External } from "../decorators/app.decorators";

@External()
@Injectable({
    providedIn: 'root',
})
export class VCPMService {
    config: any;
    constructor(
        private envService: EnvironmentService,
        public http: HttpClient
    ) {
        this.config = this.envService.config;
    }


    getUserProjectsMetadata() {
        return this.http.get(`${this.config.vcpmUrl}/users/projects/metadata`);
    }
    fetchUserProjects(): Observable<VCPM.ProjectsApiResponse> {
        let payload = {};
        return this.http.post<VCPM.ProjectsApiResponse>(`${this.config.vcpmUrl}/projects/metadata?sharingdata=true&page=0&size=500000`, payload);
    }
    fetchAllUserProjects(): Observable<VCPM.ProjectsApiResponse> {
        let payload = {};
        return this.http.post<VCPM.ProjectsApiResponse>(`${this.config.vcpmUrl}/user/projects/metadata?sharingdata=true&page=0&size=500000`, payload);
    }
    fetchProjectDataById(projectId: string) {
        return this.http.get<VCPM.Project>(`${this.config.vcpmUrl}/projects/${projectId}?sharingdata=true`);
    }
    deleteUserProject(projectId: string) {
        let payload = {};
        const isAdminRequest = false;
        return this.http.put(`${this.config.vcpmUrl}/projects/${projectId}/metadata?action=deactivate&isAdminUserRequest=${isAdminRequest}`, payload);
    }

    cloneUserProject(projectId: string) {
        return this.http.post(`${this.config.vcpmUrl}/projects/${projectId}/clone`, {});
    }

    updateProject(projectId: string, payload: any, pushToSupercollider: boolean = false) {
        if (pushToSupercollider) {
            payload = {
                ...payload,
                superColliderPush: true,
                conversionSpec: "pcs-supercollider-config",
                transformationType: "ADVANCED"
            };
        }
        return this.http.put(`${this.config.vcpmUrl}/projects?projectId=${projectId}`, payload);
    }

    shareUserProject(projectId: string, payload: any) {
        return this.http.post(`${this.config.vcpmUrl}/projects/${projectId.toString()}/collaboration`, payload);
    }

    updateProjectMetadata(project: VCPM.Project) {
        return this.http.put(`${this.config.vcpmUrl}/projects/${project.projectId}/metadata`, { metadata: project.metadata});
    }

    saveProject(metadata: any, projectData: any) {
        let data = {
            data: projectData.data ?? {},
            metadata: metadata
        };
        return this.http.post(`${this.config.vcpmUrl}/projects`, data);
    }
    updateProjectData(projectId: string, projectData: VCPM.ProjectData) {
        return this.http.put(`${this.config.vcpmUrl}/projects/${projectId}/data`, { data: projectData});
    }

    saveSupercolliderEvent(projectId: string | null, eventType: string, userId: string) {
        const payload = {
            superColliderData: {
                projectEvents: [{
                    projectId: projectId,
                    eventType: eventType,
                    eventTimestamp: new Date().toISOString(),
                    userId: userId
                }]
            }
        };
        const query = projectId ? `?projectId=${projectId}` : '';
        return this.http.put(`${this.config.vcpmUrl}/projects/supercollider${query}`, payload,
            { context: new HttpContext().set(SILENT_REQUEST, true) });
    }

    /**
     * Lock or unlock a project
     * @param projectId - The project ID
     * @param metadata - The project metadata
     * @param action - 'lock' or 'unlock'
     * @returns Observable with updated project data
     */
    updateProjectMetadataWithAction(projectId: string, metadata: any, action: 'lock' | 'unlock') {
        return this.http.put(
            `${this.config.vcpmUrl}/projects/${projectId}/metadata?action=${action}`,
            { metadata }
        );
    }

    /**
     * Lock or unlock a project (simplified version without metadata)
     * @param projectId - The project ID
     * @param action - 'lock' or 'unlock'
     * @param isAdminUserRequest - Whether this is an admin request
     * @returns Observable with updated project data
     */
    lockUnlockProject(projectId: string, action: 'lock' | 'unlock', isAdminUserRequest: boolean = false): Observable<VCPM.Project> {
        return this.http.put<VCPM.Project>(
            `${this.config.vcpmUrl}/projects/${projectId}/metadata?action=${action}&isAdminUserRequest=${isAdminUserRequest}`,
            {}
        );
    }

    /**
     * Unshare a project from a specific user
     * @param projectId - The project ID
     * @param userEmail - Email of the user to unshare from
     * @returns Observable with response
     */
    unshareProject(projectId: string, userEmail: string) {
        return this.http.put(
            `${this.config.vcpmUrl}/projects/${projectId}/collaboration/unshare`,
            { toUser: [userEmail] }
        );
    }

    /**
     * Search for users to share with
     * @param searchTerm - The search term (email or name)
     * @returns Observable with array of matching users
     */
    matchUsers(searchTerm: string): Observable<any[]> {
        const removeDomain = searchTerm.replace('@vmware.com', '');
        return this.http.get<any[]>(
            `${this.config.vcpmUrl}/external/config/user/search?searchTerm=${removeDomain.trim()}`
        );
    }

    /**
     * Send notification emails to shared users
     * @param payload - Email notification payload
     * @returns Observable with response
     */
    notifySharedUsers(payload: any): Observable<any> {
        return this.http.post(`${this.config.vcpmUrl}/notify/email`, payload);
    }
}